import type { components, Me, Page } from "@theta/api-client";
import { thetaApi, dataOf, pageOf, unwrapV1 } from "./theta";

/**
 * Authentication on /api/v1 — spec §2.1. Three things died here and are not
 * coming back:
 *
 *  - The CSRF handshake. D6: `/api/v1` has no CSRF token. A mutation proves it
 *    came from our own code with `X-Theta-Client` plus an allow-listed `Origin`,
 *    both set by `createThetaClient`. `fetchCsrfToken`, `/session/csrf.json` and
 *    the retry-on-expired-token loop went with `lib/legacy-api.ts`, and
 *    `components/CsrfInit.tsx` with them.
 *
 *  - `submit()`, the 200-with-an-error-field flattener. The legacy auth
 *    endpoints each reported failure differently — login `200` with an `error`,
 *    password reset `200` with an `errors` array, activation `200` with
 *    `success: false` — so every caller read the body both ways. v1 has one
 *    error shape and a closed vocabulary of codes, so a failure is a
 *    `ThetaError` a screen narrows with `isThetaError(e, "invalidCredentials")`.
 *
 *  - Reading the session cookie. `_t` is HttpOnly; the JS layer cannot see it
 *    and must not try. "Am I signed in?" is `GET /me` answering with a document
 *    instead of `null`, which is why signed-out is `null` and never an error
 *    (§1.6).
 *
 * Anti-enumeration is a feature of this surface, not a vagueness to improve
 * away: a wrong password and an unknown address are the same `401
 * invalidCredentials` and the miss path runs a real argon2id verification
 * against a dummy hash so they take the same time; `POST /password-resets` is
 * always `202`; and an unknown, spent or expired reset/activation token is the
 * same `410`. A screen that told those apart would undo the property.
 */

type Schemas = components["schemas"];

type AuthenticatedSession = Schemas["AuthenticatedSession"];
type Registration = Schemas["Registration"];
type RegistrationInput = Schemas["RegistrationBodyInput"];
type IdentityVerification = Schemas["IdentityVerification"];
type PublicInvite = Schemas["PublicInvite"];
/** One live login of mine — `GET /sessions`. Never carries a token or a hash. */
export type Session = Schemas["Session"];

/**
 * The signed-in user, or `null`.
 *
 * `GET /me` answers `{data: null}` when signed out (§1.6) — it is not a 403,
 * not a 404, and there is nothing to catch. The legacy version had to swallow
 * two statuses to say the same thing.
 */
export async function fetchMe(): Promise<Me | null> {
  const result = await thetaApi.me();
  // `unchanged` is only reachable when an `ifChanged` token was sent, and this
  // call sends none. The branch exists because the type is discriminated.
  return result.unchanged ? null : result.data;
}

/**
 * Sign in. Resolves to the full `Me` document — the first screen after login
 * needs no second call — and REJECTS with a `ThetaError` otherwise.
 *
 * `invalidCredentials` is deliberately the only failure a wrong login can
 * produce, for a known and an unknown account alike.
 */
export async function login(loginId: string, password: string): Promise<Me> {
  const session = dataOf<AuthenticatedSession>(
    await thetaApi.http.POST("/sessions", {
      body: { login: loginId, password },
    }),
  );
  return session.user;
}

/**
 * Sign out. Always succeeds from the caller's point of view: the route is
 * `204` even with no session at all, and a failed logout must not strand
 * somebody on a page they think they have left.
 *
 * No id and no handle — the row this request authenticated with is the row
 * that is revoked, whichever carrier it arrived on.
 */
export async function logout(): Promise<void> {
  try {
    unwrapV1<null>(await thetaApi.http.DELETE("/sessions/current", {}));
  } catch {
    // ignore — see above
  }
}

/**
 * My live logins, newest first. Cursor-paged (D4).
 *
 * NOT DEAD, THOUGH NOTHING CALLS IT YET — same standing as `useLeaveGroup`. A
 * dead-export sweep will keep finding this and `revokeSession`: the route pair
 * is live and serializes `can.revoke`, and `app/indstillinger/konto/page.tsx`
 * ships the "Aktive sessioner / Se og afslut aktive logins" row with a
 * "Kommer snart" chip where the list belongs. This is the missing half of a
 * shipped screen, not cruft. Delete it only if that row goes with it.
 */
export async function listSessions(cursor?: string | null): Promise<Page<Session>> {
  return pageOf<Session>(
    await thetaApi.http.GET("/sessions", {
      params: { query: cursor ? { cursor } : {} },
    }),
  );
}

/**
 * Revoke one session. See `listSessions` for why this has no caller yet.
 *
 * `204` for a session that is yours, one that is somebody else's and one that
 * never existed — session ids are walkable, so a status that differed would
 * count the site's live sessions for anyone who asked.
 */
export async function revokeSession(id: Session["id"] | number): Promise<void> {
  unwrapV1<null>(
    await thetaApi.http.DELETE("/sessions/{id}", {
      params: { path: { id: Number(id) } },
    }),
  );
}

/**
 * Ask for a reset link.
 *
 * ALWAYS `202`, whatever the address was. The screen says "if the account
 * exists we have sent a link", which is the literal truth and the only thing
 * that can be said without turning this into an enumeration oracle on a route
 * that needs no authentication.
 */
export async function requestPasswordReset(loginId: string): Promise<void> {
  unwrapV1<null>(
    await thetaApi.http.POST("/password-resets", { body: { login: loginId } }),
  );
}

/**
 * Redeem a reset link. The token travels in the BODY, never the path — a token
 * in a URL is a token in every access log, proxy cache and `Referer` on the
 * way here.
 *
 * Answers a signed-in session, so the screen lands on the feed rather than on
 * a login wall, and revokes every OTHER session: a reset is what somebody does
 * when they think they are compromised.
 */
export async function redeemPasswordReset(
  token: string,
  password: string,
): Promise<Me> {
  return dataOf<AuthenticatedSession>(
    await thetaApi.http.POST("/password-resets/redeem", {
      body: { token, password },
    }),
  ).user;
}

/**
 * Change my password. Requires the CURRENT one: without it a stolen cookie is
 * a stolen account in one request.
 *
 * A wrong current password is a field-level `422 validationFailed`, NOT a 401
 * — a 401 here would read as "your session died" and sign the user out for a
 * typo. Every other session is revoked; this one survives.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  unwrapV1<null>(
    await thetaApi.http.POST("/me/password", {
      body: { currentPassword, newPassword },
    }),
  );
}

/**
 * Create an account. Invite-only, which is the observed production state.
 *
 * Every refusal — no token, unknown token, spent token, a token issued to
 * another address — is the same `403` with the same sentence, and the password
 * is hashed before any of them is evaluated so they take the same time.
 */
export async function register(input: RegistrationInput): Promise<Registration> {
  return dataOf<Registration>(
    await thetaApi.http.POST("/registrations", { body: input }),
  );
}

/** Confirm an activation link and sign in. Unknown/spent/expired are one `410`. */
export async function activateAccount(token: string): Promise<Me> {
  return dataOf<AuthenticatedSession>(
    await thetaApi.http.POST("/registrations/activate", { body: { token } }),
  ).user;
}

/**
 * Bind a MitID identity to my account.
 *
 * THE FIELD IS `idToken` AND THE ROUTE IS `POST /me/identity-verification` —
 * the broker's RS256 id token, verified here against its JWKS with the
 * algorithm taken from OUR list and never from the token. The CPR is never
 * stored: what is stored is an HMAC of `sub`.
 *
 * `409 cprAlreadyBound` means the identity backs somebody else's account.
 * Re-verifying an identity already bound to YOU succeeds, so a phone retrying
 * a request it never saw the answer to is not told it failed.
 */
export async function verifyIdentity(idToken: string): Promise<IdentityVerification> {
  return dataOf<IdentityVerification>(
    await thetaApi.http.POST("/me/identity-verification", { body: { idToken } }),
  );
}

/**
 * Unbind it again — OSO-566.
 *
 * Clears the badge AND releases the identity, so the same MitID can be
 * verified afterwards, here or on another account. Own profile only; there is
 * no admin variant. Idempotent: an account that was never verified gets the
 * same 204.
 */
export async function removeIdentityVerification(): Promise<void> {
  unwrapV1<null>(await thetaApi.http.DELETE("/me/identity-verification", {}));
}

/**
 * What an invitation is for, before there is an account to check it against.
 *
 * The group's name and slug and the address it was sent to — nothing else. A
 * SPENT invitation is `410` and an unknown token `404`; the signup screen
 * renders both as "ugyldig eller allerede brugt", which is all either one is
 * allowed to say.
 */
export async function fetchInvite(token: string): Promise<PublicInvite> {
  return dataOf<PublicInvite>(
    await thetaApi.http.GET("/invites/token/{token}", {
      params: { path: { token } },
    }),
  );
}

/**
 * THE LAST COMPAT MAPPING IN THIS DOMAIN — AND IT IS SCHEDULED FOR DELETION
 *
 * `useCurrentUser()` is read by twenty-odd components spread across all four
 * domains, and this batch may only edit its own. So `toCurrentUser` still
 * answers the legacy shape, built from `Me`, and the screens THIS batch owns
 * read `Me` through `useMe()` instead.
 *
 * Two fields are lies of omission and are called out because a reader must not
 * take either at face value:
 *
 *  - `username` IS GONE FROM THE WIRE (rulings O-21/O-22). People are
 *    id-addressed. What is here is the id as a string, so the `name ||
 *    username` fallbacks in unmigrated components still render SOMETHING
 *    rather than an empty label. It is not a handle, it cannot be resolved,
 *    and no new code may read it.
 *  - `avatar_template` no longer contains `{size}`. It is `avatar.medium`, a
 *    finished URL; the surviving `.replace("{size}", …)` calls are no-ops that
 *    happen to leave a correct URL behind.
 *
 * `handoffs` names every remaining reader.
 */
export interface CurrentUser {
  id: number;
  /** NOT a handle. See above — the id as a string, for `name || username`. */
  username: string;
  name: string;
  /** A finished URL, not a template. See above. */
  avatar_template: string;
  admin?: boolean;
}

export function toCurrentUser(me: Me): CurrentUser {
  return {
    id: me.id,
    username: String(me.id),
    name: me.name ?? "",
    avatar_template: me.avatar.medium,
    // The server's own predicate, not a role the client re-derives (D11).
    admin: me.can.administerSite,
  };
}
